/**
 * EssaySlotSyncService - Syncs essay_slots with essay_requirements
 *
 * Ensures that timeline tasks (via essay_slots) stay consistent with
 * essay requirements displayed on college strategy pages.
 */

import { supabase } from '../lib/supabase';

class EssaySlotSyncService {
  /**
   * Sync essay slots for a college
   * Creates missing essay_slots based on essay_requirements
   * Updates existing slots if prompts change
   *
   * @param {string} collegeId - College ID
   * @param {string} userId - User ID
   * @returns {Promise<{created: number, updated: number, deleted: number, errors: array}>}
   */
  async syncEssaySlotsForCollege(collegeId, userId) {
    console.log(`[EssaySlotSync] Starting sync for college ${collegeId}, user ${userId}`);

    const results = {
      created: 0,
      updated: 0,
      deleted: 0,
      unchanged: 0,
      errors: []
    };

    try {
      // STEP 1: Get essay requirements for this college
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          id,
          name,
          essay_requirements
        `)
        .eq('id', collegeId)
        .single();

      if (collegeError) throw collegeError;

      if (!college.essay_requirements || !college.essay_requirements.essays) {
        console.log(`[EssaySlotSync] No essay requirements found for college ${collegeId}`);
        return results;
      }

      const essays = college.essay_requirements.essays || [];
      console.log(`[EssaySlotSync] Found ${essays.length} essay requirements`);

      // STEP 2: Get existing essay_slots for this college
      const { data: existingSlots, error: slotsError } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('college_id', collegeId)
        .eq('user_id', userId);

      if (slotsError) throw slotsError;

      console.log(`[EssaySlotSync] Found ${existingSlots?.length || 0} existing slots`);

      // Create lookup map by essay_slot number
      const slotMap = {};
      (existingSlots || []).forEach(slot => {
        slotMap[slot.essay_slot] = slot;
      });

      // STEP 3: Process each essay requirement
      for (let i = 0; i < essays.length; i++) {
        const essay = essays[i];
        const slotNumber = i + 1;

        // Skip if essay is "Not available" or has no prompt
        if (!essay.prompt || essay.prompt.toLowerCase().includes('not available')) {
          console.log(`[EssaySlotSync] Skipping slot ${slotNumber} - no prompt available`);
          continue;
        }

        const existingSlot = slotMap[slotNumber];

        if (existingSlot) {
          // Check if we need to update
          let needsUpdate = false;
          const updates = {};

          // Update prompt if changed
          if (existingSlot.prompt_text !== essay.prompt) {
            updates.prompt_text = essay.prompt;
            needsUpdate = true;
          }

          // Update word count if changed
          if (existingSlot.word_count !== essay.word_count) {
            updates.word_count = essay.word_count;
            needsUpdate = true;
          }

          // Update slot type if changed
          const slotType = this._mapEssayTypeToSlotType(essay.type);
          if (existingSlot.slot_type !== slotType) {
            updates.slot_type = slotType;
            needsUpdate = true;
          }

          if (needsUpdate) {
            // Update existing slot
            const { error: updateError } = await supabase
              .from('essay_slots')
              .update({
                ...updates,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingSlot.id);

            if (updateError) {
              results.errors.push({
                slot: slotNumber,
                error: updateError.message,
                action: 'update'
              });
            } else {
              results.updated++;
              console.log(`[EssaySlotSync] Updated slot ${slotNumber}`);
            }
          } else {
            results.unchanged++;
          }
        } else {
          // Create new slot
          const slotData = {
            user_id: userId,
            college_id: collegeId,
            essay_slot: slotNumber,
            slot_type: this._mapEssayTypeToSlotType(essay.type),
            prompt_text: essay.prompt,
            word_count: essay.word_count || null,
            status: 'empty',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error: createError } = await supabase
            .from('essay_slots')
            .upsert(slotData, {
              onConflict: 'user_id,college_id,slot_type,essay_slot'
            });

          if (createError) {
            results.errors.push({
              slot: slotNumber,
              error: createError.message,
              action: 'create'
            });
          } else {
            results.created++;
            console.log(`[EssaySlotSync] Created slot ${slotNumber}`);
            // Note: Database trigger will automatically create timeline tasks!
          }
        }
      }

      // STEP 4: Delete slots that no longer have corresponding essays
      // (Only if essay requirements have fewer essays than slots)
      const maxSlotNumber = essays.length;
      const slotsToDelete = (existingSlots || []).filter(
        slot => slot.essay_slot > maxSlotNumber
      );

      for (const slot of slotsToDelete) {
        const { error: deleteError } = await supabase
          .from('essay_slots')
          .delete()
          .eq('id', slot.id);

        if (deleteError) {
          results.errors.push({
            slot: slot.essay_slot,
            error: deleteError.message,
            action: 'delete'
          });
        } else {
          results.deleted++;
          console.log(`[EssaySlotSync] Deleted slot ${slot.essay_slot} (no longer needed)`);
        }
      }

      console.log(`[EssaySlotSync] Sync complete:`, results);
      return results;

    } catch (error) {
      console.error('[EssaySlotSync] Fatal error:', error);
      throw error;
    }
  }

  /**
   * Map essay type to slot_type
   */
  _mapEssayTypeToSlotType(essayType) {
    const mapping = {
      'supplemental': 'supplemental',
      'why_us': 'why_us',
      'why_major': 'why_major',
      'community': 'supplemental',
      'diversity': 'supplemental',
      'academic_interest': 'supplemental',
      'extracurricular': 'supplemental',
      'scholarship': 'supplemental',
      'honors_program': 'supplemental',
      'short_answer': 'supplemental',
      'other': 'supplemental'
    };

    return mapping[essayType] || 'supplemental';
  }

  /**
   * Sync all essay slots for a user across all colleges
   *
   * @param {string} userId - User ID
   * @returns {Promise<{totalCreated: number, totalUpdated: number, colleges: array}>}
   */
  async syncAllEssaySlotsForUser(userId) {
    console.log(`[EssaySlotSync] Starting full sync for user ${userId}`);

    const summary = {
      totalCreated: 0,
      totalUpdated: 0,
      totalDeleted: 0,
      colleges: []
    };

    try {
      // Get all colleges for this user
      const { data: colleges, error: collegesError } = await supabase
        .from('college_journey_colleges')
        .select('id, name')
        .eq('user_id', userId);

      if (collegesError) throw collegesError;

      // Sync each college
      for (const college of colleges) {
        try {
          const results = await this.syncEssaySlotsForCollege(college.id, userId);

          summary.totalCreated += results.created;
          summary.totalUpdated += results.updated;
          summary.totalDeleted += results.deleted;

          summary.colleges.push({
            id: college.id,
            name: college.name,
            ...results
          });
        } catch (error) {
          summary.colleges.push({
            id: college.id,
            name: college.name,
            error: error.message
          });
        }
      }

      console.log(`[EssaySlotSync] Full sync complete:`, summary);
      return summary;

    } catch (error) {
      console.error('[EssaySlotSync] Fatal error in full sync:', error);
      throw error;
    }
  }

  /**
   * Check if essay slots are out of sync
   * Returns list of colleges that need syncing
   *
   * @param {string} userId - User ID
   * @returns {Promise<array>} - Array of {collegeId, collegeName, issueCount}
   */
  async checkSyncStatus(userId) {
    const outOfSync = [];

    try {
      const { data: colleges, error } = await supabase
        .from('college_journey_colleges')
        .select('id, name, essay_requirements')
        .eq('user_id', userId);

      if (error) throw error;

      for (const college of colleges) {
        if (!college.essay_requirements?.essays) continue;

        const requirementCount = college.essay_requirements.essays.filter(
          e => e.prompt && !e.prompt.toLowerCase().includes('not available')
        ).length;

        const { count: slotCount, error: countError } = await supabase
          .from('essay_slots')
          .select('*', { count: 'exact', head: true })
          .eq('college_id', college.id)
          .eq('user_id', userId);

        if (countError) continue;

        if (requirementCount !== slotCount) {
          outOfSync.push({
            collegeId: college.id,
            collegeName: college.name,
            expectedSlots: requirementCount,
            actualSlots: slotCount || 0,
            issueCount: Math.abs(requirementCount - (slotCount || 0))
          });
        }
      }

      return outOfSync;
    } catch (error) {
      console.error('[EssaySlotSync] Error checking sync status:', error);
      throw error;
    }
  }

  /**
   * Preview what changes will happen without applying them
   * Returns detailed diff of additions, updates, and deletions
   *
   * @param {string} collegeId - College ID
   * @param {string} userId - User ID
   * @returns {Promise<{toAdd: array, toUpdate: array, toDelete: array}>}
   */
  async syncWithDiffPreview(collegeId, userId) {
    console.log(`[EssaySlotSync] Generating diff preview for college ${collegeId}`);

    try {
      // STEP 1: Get essay requirements
      const { data: college, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select('id, name, essay_requirements')
        .eq('id', collegeId)
        .single();

      if (collegeError) throw collegeError;

      const essays = college.essay_requirements?.essays || [];

      // STEP 2: Get existing essay_slots
      const { data: existingSlots, error: slotsError } = await supabase
        .from('essay_slots')
        .select('*')
        .eq('college_id', collegeId)
        .eq('user_id', userId);

      if (slotsError) throw slotsError;

      // Create lookup map
      const slotMap = {};
      (existingSlots || []).forEach(slot => {
        slotMap[slot.essay_slot] = slot;
      });

      // STEP 3: Analyze changes
      const toAdd = [];
      const toUpdate = [];
      const toDelete = [];

      // Check each essay requirement
      for (let i = 0; i < essays.length; i++) {
        const essay = essays[i];
        const slotNumber = i + 1;

        // Skip "Not available"
        if (!essay.prompt || essay.prompt.toLowerCase().includes('not available')) {
          continue;
        }

        const existingSlot = slotMap[slotNumber];

        if (existingSlot) {
          // Check if needs update
          const needsUpdate =
            existingSlot.prompt_text !== essay.prompt ||
            existingSlot.word_count !== essay.word_count ||
            existingSlot.slot_type !== this._mapEssayTypeToSlotType(essay.type);

          if (needsUpdate) {
            toUpdate.push({
              slotNumber,
              slotId: existingSlot.id,
              currentPrompt: existingSlot.prompt_text,
              newPrompt: essay.prompt,
              currentWordCount: existingSlot.word_count,
              newWordCount: essay.word_count,
              hasContent: existingSlot.status !== 'empty' || (existingSlot.content && existingSlot.content.trim().length > 0),
              status: existingSlot.status
            });
          }
        } else {
          // New essay to add
          toAdd.push({
            slotNumber,
            prompt: essay.prompt,
            wordCount: essay.word_count,
            type: this._mapEssayTypeToSlotType(essay.type),
            required: essay.required
          });
        }
      }

      // Check for slots that no longer have requirements (deletions)
      const maxSlotNumber = essays.length;
      (existingSlots || []).forEach(slot => {
        if (slot.essay_slot > maxSlotNumber) {
          toDelete.push({
            slotNumber: slot.essay_slot,
            slotId: slot.id,
            prompt: slot.prompt_text,
            hasContent: slot.status !== 'empty' || (slot.content && slot.content.trim().length > 0),
            status: slot.status,
            wordCount: slot.word_count
          });
        }
      });

      // STEP 4: Get task counts for deletions
      for (const deletion of toDelete) {
        const { count: taskCount, error: taskError } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('source_type', 'essay_slot')
          .like('source_id', `essay_slot_${deletion.slotId}%`);

        if (!taskError) {
          deletion.taskCount = taskCount || 0;
        }
      }

      console.log(`[EssaySlotSync] Diff: ${toAdd.length} to add, ${toUpdate.length} to update, ${toDelete.length} to delete`);

      return {
        toAdd,
        toUpdate,
        toDelete,
        collegeName: college.name,
        summary: {
          additions: toAdd.length,
          updates: toUpdate.length,
          deletions: toDelete.length,
          hasChanges: toAdd.length > 0 || toUpdate.length > 0 || toDelete.length > 0
        }
      };

    } catch (error) {
      console.error('[EssaySlotSync] Error generating diff:', error);
      throw error;
    }
  }

  /**
   * Convert essay slot to unslotted draft to preserve student work
   *
   * @param {object} essaySlot - Essay slot to preserve
   * @returns {Promise<object|null>} - Preserved draft or null if no content
   */
  async convertSlotToUnslottedDraft(essaySlot) {
    console.log(`[EssaySlotSync] Checking if slot ${essaySlot.id} needs preservation`);

    // Check if has content worth preserving
    const hasContent = essaySlot.status !== 'empty' ||
                       (essaySlot.content && essaySlot.content.trim().length > 0);

    if (!hasContent) {
      console.log(`[EssaySlotSync] Slot ${essaySlot.id} has no content, skipping preservation`);
      return null;
    }

    try {
      // Update the slot to become unslotted
      const { data, error } = await supabase
        .from('essay_slots')
        .update({
          college_id: null,        // Remove college association
          essay_slot: null,        // Remove slot number
          slot_type: 'unslotted',  // Mark as unslotted
          prompt_text: `[Preserved] ${essaySlot.prompt_text}`, // Prefix for clarity
          updated_at: new Date().toISOString()
        })
        .eq('id', essaySlot.id)
        .select()
        .single();

      if (error) throw error;

      console.log(`[EssaySlotSync] Successfully preserved slot ${essaySlot.id} as unslotted draft`);
      return data;
    } catch (error) {
      console.error(`[EssaySlotSync] Error preserving slot ${essaySlot.id}:`, error);
      throw error;
    }
  }

  /**
   * Sync with user confirmation for deletions
   * Preserves draft content when deleting slots
   *
   * @param {string} collegeId - College ID
   * @param {string} userId - User ID
   * @param {boolean} confirmed - User confirmed deletions
   * @returns {Promise<object>} - Results with preserved drafts count
   */
  async syncWithConfirmation(collegeId, userId, confirmed = false) {
    console.log(`[EssaySlotSync] Starting sync with confirmation for college ${collegeId}`);

    // First get the diff to see if we need confirmation
    const diff = await this.syncWithDiffPreview(collegeId, userId);

    // If there are deletions and not confirmed, return the diff for user review
    if (diff.toDelete.length > 0 && !confirmed) {
      console.log(`[EssaySlotSync] Deletions detected, requiring confirmation`);
      return {
        needsConfirmation: true,
        diff,
        message: 'Please confirm deletion of essay slots and associated tasks'
      };
    }

    // User confirmed or no deletions, proceed with sync
    const results = {
      created: 0,
      updated: 0,
      deleted: 0,
      preserved: 0,
      unchanged: 0,
      errors: []
    };

    try {
      const { data: college } = await supabase
        .from('college_journey_colleges')
        .select('id, name, essay_requirements')
        .eq('id', collegeId)
        .single();

      const essays = college.essay_requirements?.essays || [];

      // Process additions
      for (const addition of diff.toAdd) {
        try {
          const slotData = {
            user_id: userId,
            college_id: collegeId,
            essay_slot: addition.slotNumber,
            slot_type: addition.type,
            prompt_text: addition.prompt,
            word_count: addition.wordCount || null,
            status: 'empty',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error: createError } = await supabase
            .from('essay_slots')
            .upsert(slotData, {
              onConflict: 'user_id,college_id,slot_type,essay_slot'
            });

          if (createError) {
            results.errors.push({
              slot: addition.slotNumber,
              error: createError.message,
              action: 'create'
            });
          } else {
            results.created++;
            console.log(`[EssaySlotSync] Created slot ${addition.slotNumber}`);
          }
        } catch (error) {
          results.errors.push({
            slot: addition.slotNumber,
            error: error.message,
            action: 'create'
          });
        }
      }

      // Process updates
      for (const update of diff.toUpdate) {
        try {
          const updates = {
            prompt_text: update.newPrompt,
            word_count: update.newWordCount,
            updated_at: new Date().toISOString()
          };

          const { error: updateError } = await supabase
            .from('essay_slots')
            .update(updates)
            .eq('id', update.slotId);

          if (updateError) {
            results.errors.push({
              slot: update.slotNumber,
              error: updateError.message,
              action: 'update'
            });
          } else {
            results.updated++;
            console.log(`[EssaySlotSync] Updated slot ${update.slotNumber}`);
          }
        } catch (error) {
          results.errors.push({
            slot: update.slotNumber,
            error: error.message,
            action: 'update'
          });
        }
      }

      // Process deletions (with preservation)
      for (const deletion of diff.toDelete) {
        try {
          // Get the full slot data
          const { data: slotData } = await supabase
            .from('essay_slots')
            .select('*')
            .eq('id', deletion.slotId)
            .single();

          if (slotData) {
            // Try to preserve as unslotted draft
            const preserved = await this.convertSlotToUnslottedDraft(slotData);

            if (preserved) {
              results.preserved++;
              console.log(`[EssaySlotSync] Preserved draft for slot ${deletion.slotNumber}`);
              // Don't delete the slot since we converted it to unslotted
            } else {
              // No content to preserve, safe to delete
              const { error: deleteError } = await supabase
                .from('essay_slots')
                .delete()
                .eq('id', deletion.slotId);

              if (deleteError) {
                results.errors.push({
                  slot: deletion.slotNumber,
                  error: deleteError.message,
                  action: 'delete'
                });
              } else {
                results.deleted++;
                console.log(`[EssaySlotSync] Deleted slot ${deletion.slotNumber}`);
              }
            }
          }
        } catch (error) {
          results.errors.push({
            slot: deletion.slotNumber,
            error: error.message,
            action: 'delete'
          });
        }
      }

      console.log(`[EssaySlotSync] Sync complete:`, results);
      return {
        needsConfirmation: false,
        results,
        diff
      };

    } catch (error) {
      console.error('[EssaySlotSync] Fatal error:', error);
      throw error;
    }
  }
  /**
   * Generate missing timeline tasks for essay slots
   * Creates the 6 workflow tasks for essays that don't have them yet
   *
   * @param {string} userId - User ID
   * @returns {Promise<{tasksCreated: number, essaysProcessed: number, colleges: array}>}
   */
  async generateMissingTimelineTasks(userId) {
    console.log(`[EssaySlotSync] Generating missing timeline tasks for user ${userId}`);

    const summary = {
      tasksCreated: 0,
      essaysProcessed: 0,
      colleges: []
    };

    try {
      // Get essay workflow templates from milestone_templates (matching the trigger logic)
      // Uses metadata-based query like the trigger does
      const { data: templates, error: templatesError } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('is_active', true)
        .eq('category', 'essay')
        .filter('metadata->>workflow_type', 'eq', 'essay')
        .gte('metadata->>task_sequence', '1')
        .lte('metadata->>task_sequence', '7')
        .order('metadata->>task_sequence');

      if (templatesError) throw templatesError;

      if (!templates || templates.length === 0) {
        console.warn('[EssaySlotSync] No essay workflow templates found');
        return summary;
      }

      console.log(`[EssaySlotSync] Found ${templates.length} essay workflow templates`);

      // Get all colleges for this user
      const { data: colleges, error: collegesError} = await supabase
        .from('college_journey_colleges')
        .select(`
          id,
          name,
          rd_deadline_manual,
          ed_deadline_manual,
          ea_deadline_manual,
          global_college:global_colleges(rd_deadline, ed_deadline, ea_deadline)
        `)
        .eq('user_id', userId);

      if (collegesError) throw collegesError;

      for (const college of colleges) {
        const collegeResults = { name: college.name, tasksCreated: 0, essaysProcessed: 0 };

        // Get application deadline
        const applicationDeadline =
          college.rd_deadline_manual ||
          college.ed_deadline_manual ||
          college.ea_deadline_manual ||
          college.global_college?.rd_deadline ||
          college.global_college?.ed_deadline ||
          college.global_college?.ea_deadline;

        // Get all essay_slots for this college
        const { data: essaySlots, error: slotsError } = await supabase
          .from('essay_slots')
          .select('*')
          .eq('college_id', college.id)
          .eq('user_id', userId);

        if (slotsError) {
          console.error(`Error fetching essay slots for college ${college.id}:`, slotsError);
          continue;
        }

        for (const slot of essaySlots || []) {
          // Check if tasks already exist
          const { data: existingTasks } = await supabase
            .from('timeline_items')
            .select('id')
            .like('source_id', `essay_slot_${slot.id}_task_%`);

          if (existingTasks && existingTasks.length > 0) {
            console.log(`Skipping essay slot ${slot.id} (already has ${existingTasks.length} tasks)`);
            continue;
          }

          // Determine essay topic (matching trigger logic - truncate to 60 chars)
          let essayTopic = slot.friendly_title || slot.prompt_text || 'Supplemental Essay';
          if (essayTopic.length > 60) {
            essayTopic = essayTopic.substring(0, 57) + '...';
          }

          // Determine workflow keys (matching trigger logic)
          let workflowKey, subWorkflowKey;
          if (slot.slot_type === 'personal_statement' || !college.id) {
            workflowKey = 'write_personal_statement';
            subWorkflowKey = 'draft_your_essay';
          } else {
            // College-specific workflow
            const collegeSlug = college.name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_');
            workflowKey = `complete_${collegeSlug}_app`;

            // Generate sub_workflow_key from prompt
            if (slot.prompt_text) {
              let promptSlug = slot.prompt_text.substring(0, 50).toLowerCase();
              promptSlug = promptSlug.replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_').replace(/_+$/g, '');
              if (promptSlug.length > 40) {
                promptSlug = promptSlug.substring(0, 40);
              }
              subWorkflowKey = `essay_${promptSlug}`;
            } else {
              // Fallback: use sequence number
              subWorkflowKey = `essay_${slot.essay_slot || 1}`;
            }
          }

          // Generate tasks from templates (matching trigger logic)
          const tasksToInsert = templates.map(template => {
            const taskSequence = template.metadata?.task_sequence || template.task_sequence;

            // Replace placeholders in title and description
            let taskTitle = template.title.replace('[ESSAY_TOPIC]', essayTopic).replace('[COLLEGE_NAME]', college.name);
            let taskDescription = template.description?.replace('[ESSAY_TOPIC]', essayTopic).replace('[COLLEGE_NAME]', college.name);

            // Calculate due date based on template offset
            let dueDate = applicationDeadline;
            if (applicationDeadline && template.offset_days) {
              const deadline = new Date(applicationDeadline);
              deadline.setDate(deadline.getDate() + template.offset_days);
              // Ensure not in the past
              if (deadline < new Date()) {
                deadline.setDate(new Date().getDate() + 1);
              }
              dueDate = deadline.toISOString().split('T')[0];
            }

            return {
              user_id: userId,
              college_id: college.id,
              source_type: 'essay_slot',
              source_id: `essay_slot_${slot.id}_task_${taskSequence}`,
              category: 'essay',
              title: taskTitle,
              description: taskDescription,
              scheduled_date: dueDate,
              due_date: dueDate,
              hard_deadline: applicationDeadline,
              status: 'planned',
              priority: template.priority || 'high',
              tags: ['essay_workflow', 'auto_generated'],
              module_link: template.module_link || '/college/essays',
              workflow_key: workflowKey,
              sub_workflow_key: subWorkflowKey,
              hierarchy_level: 3, // Step level
              display_order: taskSequence,
              workflow_metadata: {
                contextual_tip: template.workflow_metadata?.contextual_tip,
                ai_tool: template.metadata?.ai_tool_name,
                ai_tool_route: template.metadata?.ai_tool_route
              },
              task_tip: template.task_tip,
              metadata: {
                essay_slot_id: slot.id,
                college_name: college.name,
                essay_topic: essayTopic,
                template_id: template.id,
                task_sequence: taskSequence,
                has_ai_tool: template.metadata?.has_ai_tool,
                essay_prompt: slot.prompt_text,
                word_limit: slot.word_limit,
                friendly_title: slot.friendly_title || null,
                slot_type: slot.slot_type,
                generated_by_admin: true,
                generated_at: new Date().toISOString()
              }
            };
          });

          const { data, error } = await supabase
            .from('timeline_items')
            .insert(tasksToInsert);

          if (error) {
            console.error(`Error creating tasks for essay slot ${slot.id}:`, error);
          } else {
            collegeResults.tasksCreated += tasksToInsert.length;
            collegeResults.essaysProcessed += 1;
          }
        }

        if (collegeResults.tasksCreated > 0) {
          summary.tasksCreated += collegeResults.tasksCreated;
          summary.essaysProcessed += collegeResults.essaysProcessed;
          summary.colleges.push(collegeResults);
        }
      }

      console.log(`[EssaySlotSync] Generated ${summary.tasksCreated} timeline tasks for ${summary.essaysProcessed} essays`);
      return summary;
    } catch (error) {
      console.error('[EssaySlotSync] Error generating timeline tasks:', error);
      throw error;
    }
  }

  /**
   * Update timeline task titles to reflect current friendly_title or prompt
   *
   * @param {string} userId - User ID
   * @returns {Promise<{tasksUpdated: number, essaysProcessed: number, colleges: array}>}
   */
  async updateEssayTaskTitles(userId) {
    console.log(`[EssaySlotSync] Updating essay task titles for user ${userId}`);

    const summary = {
      tasksUpdated: 0,
      essaysProcessed: 0,
      colleges: []
    };

    try {
      // Get all colleges for this user
      const { data: colleges, error: collegesError } = await supabase
        .from('college_journey_colleges')
        .select('id, name')
        .eq('user_id', userId);

      if (collegesError) throw collegesError;

      for (const college of colleges) {
        const collegeResults = { name: college.name, tasksUpdated: 0, essaysProcessed: 0 };

        // Get all essay_slots for this college
        const { data: essaySlots, error: slotsError } = await supabase
          .from('essay_slots')
          .select('*')
          .eq('college_id', college.id)
          .eq('user_id', userId);

        if (slotsError) {
          console.error(`Error fetching essay slots for college ${college.id}:`, slotsError);
          continue;
        }

        for (const slot of essaySlots || []) {
          // Get all timeline_items for this essay_slot
          const { data: tasks, error: tasksError } = await supabase
            .from('timeline_items')
            .select('id, title, metadata')
            .like('source_id', `essay_slot_${slot.id}_task_%`);

          if (tasksError || !tasks || tasks.length === 0) {
            continue;
          }

          const essayIdentifier = slot.friendly_title || (slot.prompt_text?.substring(0, 50) || 'Supplemental Essay');
          const essaySuffix = slot.friendly_title ? '' : '...';

          for (const task of tasks) {
            // Extract task type prefix
            const taskTypeMatch = task.title.match(/^(Brainstorm Concepts|Write First Draft|Generate First Draft Analysis|Get Essay Feedback|Revise and Polish Essay|Finalize Essay)/);
            const taskType = taskTypeMatch ? taskTypeMatch[1] : '';

            const newTitle = taskType ? `${taskType}: ${essayIdentifier}${essaySuffix}` : task.title;

            // Update metadata
            const updatedMetadata = {
              ...(task.metadata || {}),
              essay_prompt: slot.prompt_text,
              word_limit: slot.word_limit,
              friendly_title: slot.friendly_title || null
            };

            const { error: updateError } = await supabase
              .from('timeline_items')
              .update({
                title: newTitle,
                metadata: updatedMetadata
              })
              .eq('id', task.id);

            if (!updateError) {
              collegeResults.tasksUpdated += 1;
            }
          }

          if (tasks.length > 0) {
            collegeResults.essaysProcessed += 1;
          }
        }

        if (collegeResults.tasksUpdated > 0) {
          summary.tasksUpdated += collegeResults.tasksUpdated;
          summary.essaysProcessed += collegeResults.essaysProcessed;
          summary.colleges.push(collegeResults);
        }
      }

      console.log(`[EssaySlotSync] Updated ${summary.tasksUpdated} timeline task titles for ${summary.essaysProcessed} essays`);
      return summary;
    } catch (error) {
      console.error('[EssaySlotSync] Error updating task titles:', error);
      throw error;
    }
  }

  /**
   * Update ALL fields in existing essay tasks from templates
   * This refreshes priority, module_link, workflow fields, task_tip, etc.
   * Useful after updating milestone_templates (e.g., adding 7th step, changing modules)
   *
   * @param {string} userId - User ID
   * @returns {Promise<{tasksUpdated: number, essaysProcessed: number, colleges: array}>}
   */
  async updateEssayTasksFromTemplates(userId) {
    console.log(`[EssaySlotSync] Updating essay tasks from templates for user ${userId}`);

    const summary = {
      tasksUpdated: 0,
      essaysProcessed: 0,
      colleges: []
    };

    try {
      // Get essay workflow templates
      const { data: templates, error: templatesError } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('is_active', true)
        .eq('category', 'essay')
        .filter('metadata->>workflow_type', 'eq', 'essay')
        .gte('metadata->>task_sequence', '1')
        .lte('metadata->>task_sequence', '7')
        .order('metadata->>task_sequence');

      if (templatesError) throw templatesError;

      if (!templates || templates.length === 0) {
        console.warn('[EssaySlotSync] No essay workflow templates found');
        return summary;
      }

      console.log(`[EssaySlotSync] Found ${templates.length} essay workflow templates`);

      // Create a map of templates by task_sequence for quick lookup
      const templateMap = new Map();
      templates.forEach(template => {
        const taskSequence = template.metadata?.task_sequence || template.task_sequence;
        templateMap.set(taskSequence.toString(), template);
      });

      // Get all colleges for this user
      const { data: colleges, error: collegesError } = await supabase
        .from('college_journey_colleges')
        .select('id, name')
        .eq('user_id', userId);

      if (collegesError) throw collegesError;

      for (const college of colleges) {
        const collegeResults = { name: college.name, tasksUpdated: 0, essaysProcessed: 0 };

        // Get all essay_slots for this college
        const { data: essaySlots, error: slotsError } = await supabase
          .from('essay_slots')
          .select('*')
          .eq('college_id', college.id)
          .eq('user_id', userId);

        if (slotsError) {
          console.error(`Error fetching essay slots for college ${college.id}:`, slotsError);
          continue;
        }

        for (const slot of essaySlots || []) {
          // Get all timeline_items for this essay_slot
          const { data: tasks, error: tasksError } = await supabase
            .from('timeline_items')
            .select('id, source_id, title, metadata')
            .like('source_id', `essay_slot_${slot.id}_task_%`);

          if (tasksError || !tasks || tasks.length === 0) {
            continue;
          }

          // Determine essay topic (matching trigger logic)
          let essayTopic = slot.friendly_title || slot.prompt_text || 'Supplemental Essay';
          if (essayTopic.length > 60) {
            essayTopic = essayTopic.substring(0, 57) + '...';
          }

          // Determine workflow keys
          let workflowKey, subWorkflowKey;
          if (slot.slot_type === 'personal_statement' || !college.id) {
            workflowKey = 'write_personal_statement';
            subWorkflowKey = 'draft_your_essay';
          } else {
            const collegeSlug = college.name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_');
            workflowKey = `complete_${collegeSlug}_app`;

            if (slot.prompt_text) {
              let promptSlug = slot.prompt_text.substring(0, 50).toLowerCase();
              promptSlug = promptSlug.replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_').replace(/_+$/g, '');
              if (promptSlug.length > 40) {
                promptSlug = promptSlug.substring(0, 40);
              }
              subWorkflowKey = `essay_${promptSlug}`;
            } else {
              subWorkflowKey = `essay_${slot.essay_slot || 1}`;
            }
          }

          for (const task of tasks) {
            // Extract task_sequence from source_id (e.g., essay_slot_xxx_task_3 → 3)
            const sourceIdMatch = task.source_id.match(/_task_(\d+)$/);
            if (!sourceIdMatch) continue;

            const taskSequence = sourceIdMatch[1];
            const template = templateMap.get(taskSequence);

            if (!template) {
              console.warn(`No template found for task_sequence ${taskSequence}`);
              continue;
            }

            // Build updated title and description with placeholders replaced
            const updatedTitle = template.title
              .replace('[ESSAY_TOPIC]', essayTopic)
              .replace('[COLLEGE_NAME]', college.name);

            const updatedDescription = template.description
              ?.replace('[ESSAY_TOPIC]', essayTopic)
              .replace('[COLLEGE_NAME]', college.name);

            // Build updated metadata
            const updatedMetadata = {
              ...(task.metadata || {}),
              essay_prompt: slot.prompt_text,
              word_limit: slot.word_limit,
              friendly_title: slot.friendly_title || null,
              college_name: college.name,
              essay_topic: essayTopic,
              template_id: template.id,
              task_sequence: taskSequence,
              has_ai_tool: template.metadata?.has_ai_tool,
              slot_type: slot.slot_type
            };

            // Update task with ALL fields from template
            const { error: updateError } = await supabase
              .from('timeline_items')
              .update({
                title: updatedTitle,
                description: updatedDescription,
                priority: template.priority || 'high',
                module_link: template.module_link || '/college/essays',
                workflow_key: workflowKey,
                sub_workflow_key: subWorkflowKey,
                hierarchy_level: 3,
                display_order: parseInt(taskSequence),
                workflow_metadata: {
                  contextual_tip: template.workflow_metadata?.contextual_tip,
                  ai_tool: template.metadata?.ai_tool_name,
                  ai_tool_route: template.metadata?.ai_tool_route
                },
                task_tip: template.task_tip,
                metadata: updatedMetadata
              })
              .eq('id', task.id);

            if (!updateError) {
              collegeResults.tasksUpdated += 1;
            } else {
              console.error(`Error updating task ${task.id}:`, updateError);
            }
          }

          if (tasks.length > 0) {
            collegeResults.essaysProcessed += 1;
          }
        }

        if (collegeResults.tasksUpdated > 0) {
          summary.tasksUpdated += collegeResults.tasksUpdated;
          summary.essaysProcessed += collegeResults.essaysProcessed;
          summary.colleges.push(collegeResults);
        }
      }

      console.log(`[EssaySlotSync] Updated ${summary.tasksUpdated} timeline tasks from templates for ${summary.essaysProcessed} essays`);
      return summary;
    } catch (error) {
      console.error('[EssaySlotSync] Error updating tasks from templates:', error);
      throw error;
    }
  }
}

export default new EssaySlotSyncService();
